import {
  faComment,
  faCommentSlash,
  faHeart,
  faPen,
  faThumbtack,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faHeart as faHeartSolid,
  faThumbtack as faThumbtackSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioPlayer } from 'src/components/AudioPlayer';
import { Avatar } from 'src/components/Avatar';
import { Badge } from 'src/components/Badge';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import ImageAttachment from 'src/components/ImageAttachment';
import { Link } from 'src/components/Link';
import {
  MentionTextField,
  type MentionToken,
  encodeMentions,
} from 'src/components/MentionTextField';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { Video as VideoPlayer } from 'src/components/Video';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { Activity, ActivityType } from 'src/models/activity/_types';
import {
  addActivityComment,
  deleteActivityComment,
  deleteActivityPost,
  editActivityPost,
  toggleActivityLike,
  toggleDisableComments,
  togglePinActivity,
} from 'src/models/activity/activity';
import {
  searchMentionableGroupMembers,
  searchMentionableUsers,
} from 'src/models/user/userSearch';

import { TimeAgo, TimeAgoProps } from '../TimeAgo';

import { ActivityComment } from './ActivityComment';
import { ActivityContent } from './ActivityContent';

import type { ActivityComment as ActivityCommentModel } from 'src/models/activity/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface ActivityItemProps {
  activity: Activity;
  /** When set (e.g. group discussion), @mention search is limited to this group's members. */
  mentionSearchGroupId?: Identifier | null;
  canManage?: boolean;
  pendingCommentCount?: number;
  onShowNewComments?: () => void;
  dateFormatter?: TimeAgoProps['format'];
  // If true, only show the created at time
  onlyCreatedAt?: boolean;
  // If true, show the note
  showNote?: boolean;
  variant?: 'default' | 'mobile';
}

export const ActivityItem = ({
  activity,
  mentionSearchGroupId,
  canManage,
  pendingCommentCount = 0,
  onShowNewComments,
  dateFormatter = 'relative',
  onlyCreatedAt = false,
  showNote = true,
  variant = 'default',
}: ActivityItemProps) => {
  const isMobile = variant === 'mobile';
  const { user, session } = useSession();
  const queryClient = useQueryClient();
  const { t } = useTranslation('translation', { keyPrefix: 'activity' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const [showComments, setShowComments] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [commentMentions, setCommentMentions] = useState<MentionToken[]>([]);
  const [liked, setLiked] = useState(
    (activity.userLikes || []).includes(Number(user.id))
  );
  const [likesCount, setLikesCount] = useState(activity.likesCount);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(activity.dataString || '');

  const date = onlyCreatedAt
    ? activity.createdAt
    : activity.data.workoutDate
      ? new Date(activity.data.workoutDate)
      : activity.createdAt;

  const toggleLikeMutation = useMutation({
    mutationFn: () => toggleActivityLike(String(activity.id), !liked),
    onMutate: () => {
      setLiked(!liked);
      setLikesCount(liked ? likesCount - 1 : likesCount + 1);
    },
    onError: () => {
      setLiked(liked);
      setLikesCount(likesCount);
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ text }: { text: string }) =>
      addActivityComment(String(activity.id), text),
    onSuccess: () => {
      setComment('');
      setCommentMentions([]);
      // Keep comment box open so user can post multiple comments
      queryClient.invalidateQueries({
        queryKey: ['activities'],
      });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const togglePinMutation = useMutation({
    mutationFn: () => togglePinActivity(String(activity.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const toggleDisableCommentsMutation = useMutation({
    mutationFn: () => toggleDisableComments(String(activity.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: () => deleteActivityPost(String(activity.id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const editPostMutation = useMutation({
    mutationFn: () => editActivityPost(String(activity.id), editValue),
    onSuccess: () => {
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string | number) =>
      deleteActivityComment(String(activity.id), commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({
        queryKey: ['group-discussion-activities'],
      });
    },
  });

  const handleDeleteComment = useCallback(
    async (commentId: string | number) => {
      // For now, delete comment directly without confirmation
      // Could add a similar popover approach if needed
      await deleteCommentMutation.mutateAsync(commentId);
    },
    [deleteCommentMutation]
  );

  const mentionTrainerId = session?.trainerId ?? user?.id;

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = comment.trim();
    if (trimmed.length === 0) return;
    const payload = encodeMentions(comment, commentMentions).trim();
    if (payload.length === 0) return;
    addCommentMutation.mutate({ text: payload });
  };

  const handleCommentClick = () => {
    const nextShow = !showComments;
    setShowComments(nextShow);
    if (nextShow) {
      setShowCommentBox(true);
      if (pendingCommentCount > 0 && onShowNewComments) onShowNewComments();
    } else {
      setShowCommentBox(false);
    }
  };

  // Only I can edit my own posts
  const canEdit =
    String(activity.activityType) === String(ActivityType.GroupActivity) &&
    String(activity.userId) === String(user.id);
  const canDelete = canManage || String(activity.userId) === String(user.id);

  const [deleteMenuOpen, setDeleteMenuOpen] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    setDeleteMenuOpen(false);
    await deletePostMutation.mutateAsync();
  }, [deletePostMutation]);

  const handleSaveEdit = useCallback(async () => {
    await editPostMutation.mutateAsync();
  }, [editPostMutation]);

  const displayedCommentsCount = useMemo(() => {
    return (activity.comments?.length || 0) + (pendingCommentCount || 0);
  }, [activity.comments?.length, pendingCommentCount]);

  const sortedComments = useMemo(() => {
    const getTs = (c: ActivityCommentModel) => {
      const v: any = c.createdAt as any;
      if (typeof v === 'number') return v;
      const parsed = Date.parse(String(v));
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    return [...(activity.comments || [])].sort((a, b) => getTs(b) - getTs(a));
  }, [activity.comments]);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <>
      <Card component={isMobile ? 'article' : 'div'} aria-label={isMobile ? t('activityPost', { defaultValue: 'Activity post' }) : undefined}>
        {activity.pinned && (
          <Box
            sx={(theme) => ({
              px: 3,
              pt: 2,
              color: theme.vars.palette.primary.main,
            })}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon icon={faThumbtackSolid} />
              <Typography variant="caption" fontWeight="semibold">
                {t('pinned', { defaultValue: 'Pinned' })}
              </Typography>
            </Stack>
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          p={2}
          alignItems="center"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              src={activity.userAvatar}
              sx={isMobile ? { width: 40, height: 40 } : { width: 24, height: 24 }}
            />
            <Typography fontWeight="semibold">{activity.userName}</Typography>
          </Stack>
          <Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              component={Link}
              href={externalLinks.v2Url(`/activity/${activity.id}`)}
              target="_blank"
              sx={{ textAlign: 'right' }}
            >
              <TimeAgo timestamp={date} format={dateFormatter} />
            </Typography>
            {activity.viaLink && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {t('via')}
                </Typography>
                <HtmlTypography
                  sx={{
                    '& a': {
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    },
                  }}
                >
                  {activity.viaLink}
                </HtmlTypography>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Box p={2} sx={{ wordBreak: 'break-word' }}>
          {showNote && !isEditing && activity.data.notes && (
            <Typography gutterBottom>{activity.data.notes}</Typography>
          )}

          {!isEditing && activity.string && (
            <ActivityContent content={activity.string} />
          )}
          {!isEditing &&
            Array.isArray(activity.videos) &&
            activity.videos.length > 0 && (
              <Stack spacing={1} mt={1}>
                {activity.videos.map((v) => (
                  <Box key={v.id} sx={{ maxWidth: 640, width: '100%' }}>
                    <VideoPlayer
                      videoId={v.id as any}
                      playbackId={v.playbackId}
                      downloadable={!!v.downloadable}
                      embedUrl={v.url}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          {!isEditing && activity.voiceMemo && (
            <Box mt={1}>
              <AudioPlayer url={activity.voiceMemo} />
            </Box>
          )}
          {!isEditing &&
            Array.isArray(activity.images) &&
            activity.images.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                mt={1}
                sx={{ flexWrap: 'wrap' }}
              >
                {activity.images.map((img, i) => (
                  <Box key={i} sx={{ maxWidth: 500 }}>
                    <ImageAttachment
                      url={img.thumb || img.url}
                      fullSizeUrl={img.url}
                      maxWidth={300}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          {isEditing && (
            <Box>
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                multiline
                minRows={3}
                sx={{ width: '100%' }}
              />
              <Stack
                direction="row"
                spacing={1}
                mt={1}
                justifyContent="flex-end"
              >
                <Button size="small" onClick={() => setIsEditing(false)}>
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  size="small"
                  variant="prominent"
                  onClick={handleSaveEdit}
                  disabled={!editValue.trim()}
                >
                  {tCommon('buttons.save')}
                </Button>
              </Stack>
            </Box>
          )}
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Stack
            direction="row"
            p={isMobile ? 2 : 1}
            spacing={3}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                direction="row"
                component={Stack}
                alignItems="center"
                spacing={1}
                onClick={() => toggleLikeMutation.mutate()}
                aria-label={
                  liked
                    ? t('unlike', { defaultValue: 'Unlike' })
                    : t('like', { defaultValue: 'Like' })
                }
                sx={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}
              >
                <Badge
                  badgeContent={likesCount}
                  color="primary"
                  invisible={!mdDown || likesCount === 0}
                >
                  <Icon icon={liked ? faHeartSolid : faHeart} />
                </Badge>
                {!mdDown && (
                  <Typography variant="body2" color="text.primary">
                    {t('likes.count', { count: likesCount })}
                  </Typography>
                )}
              </Button>
              <Button
                direction="row"
                component={Stack}
                alignItems="center"
                spacing={1}
                onClick={handleCommentClick}
                disabled={activity.disableComments}
                aria-label={t('viewComments', {
                  defaultValue: 'View comments',
                  count: displayedCommentsCount,
                })}
                sx={isMobile ? { minWidth: 44, minHeight: 44 } : undefined}
              >
                <Badge
                  badgeContent={displayedCommentsCount}
                  color="primary"
                  invisible={!mdDown || displayedCommentsCount === 0}
                >
                  {activity.disableComments ? (
                    <Icon icon={faCommentSlash} />
                  ) : (
                    <Icon icon={faComment} />
                  )}
                </Badge>
                {!mdDown && (
                  <Typography variant="body2" color="text.primary">
                    {t('comments.count', { count: displayedCommentsCount })}
                  </Typography>
                )}
              </Button>
            </Stack>

            {(canManage || canEdit || canDelete) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                {canEdit && (
                  <IconButton
                    onClick={() => setIsEditing((v) => !v)}
                    size="small"
                    aria-label={t('edit', { defaultValue: 'Edit' })}
                  >
                    <Icon icon={faPen} />
                  </IconButton>
                )}
                {canManage && (
                  <>
                    <IconButton
                      onClick={() => togglePinMutation.mutate()}
                      size="small"
                      color={activity.pinned ? 'primary' : 'default'}
                      aria-label={
                        activity.pinned
                          ? t('unpin', { defaultValue: 'Unpin' })
                          : t('pin', { defaultValue: 'Pin' })
                      }
                    >
                      <Icon icon={faThumbtack} />
                    </IconButton>
                    <IconButton
                      onClick={() => toggleDisableCommentsMutation.mutate()}
                      size="small"
                      color={activity.disableComments ? 'primary' : 'default'}
                      aria-label={
                        activity.disableComments
                          ? t('enableComments', {
                              defaultValue: 'Enable comments',
                            })
                          : t('disableComments', {
                              defaultValue: 'Disable comments',
                            })
                      }
                    >
                      <Icon icon={faCommentSlash} />
                    </IconButton>
                  </>
                )}
                {canDelete && (
                  <DropdownMenu
                    open={deleteMenuOpen}
                    setOpen={setDeleteMenuOpen}
                    closeOnContentClick={false}
                    trigger={
                      <IconButton
                        size="small"
                        aria-label={t('delete', { defaultValue: 'Delete' })}
                      >
                        <Icon icon={faTrash} />
                      </IconButton>
                    }
                  >
                    <Box p={2} sx={{ minWidth: 250 }}>
                      <Typography variant="h6" gutterBottom>
                        {t('deletePost', { defaultValue: 'Delete Post' })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t('deletePostConfirmation', {
                          defaultValue:
                            'Are you sure you want to delete this discussion post? This action cannot be undone.',
                        })}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        mt={2}
                        justifyContent="flex-end"
                      >
                        <Button
                          size="small"
                          onClick={() => setDeleteMenuOpen(false)}
                        >
                          {t('cancel', { defaultValue: 'Cancel' })}
                        </Button>
                        <Button
                          size="small"
                          variant="prominent"
                          color="error"
                          onClick={handleConfirmDelete}
                        >
                          {t('delete', { defaultValue: 'Delete' })}
                        </Button>
                      </Stack>
                    </Box>
                  </DropdownMenu>
                )}
              </Stack>
            )}
          </Stack>

          {showComments && !activity.disableComments && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
              {showCommentBox && (
                <form onSubmit={handleSubmitComment}>
                  <Stack direction="row" spacing={2} mb={2}>
                    <MentionTextField
                      value={comment}
                      onChange={setComment}
                      mentions={commentMentions}
                      onMentionsChange={setCommentMentions}
                      onSearch={(term, signal) =>
                        mentionSearchGroupId != null &&
                        mentionSearchGroupId !== ''
                          ? searchMentionableGroupMembers({
                              groupId: mentionSearchGroupId,
                              term,
                              signal,
                            })
                          : searchMentionableUsers({
                              term,
                              trainerId: mentionTrainerId,
                              signal,
                            })
                      }
                      placeholder={t('addComment')}
                      size="small"
                      sx={{
                        flex: 1,
                        ...(isMobile && {
                          '& .MuiInputBase-root': { minHeight: 44 },
                        }),
                      }}
                    />
                    <Button
                      size="small"
                      color="primary"
                      onClick={handleSubmitComment}
                      disabled={!comment.trim()}
                      sx={isMobile ? { minHeight: 44 } : undefined}
                    >
                      {tCommon('buttons.post')}
                    </Button>
                  </Stack>
                </form>
              )}
              {pendingCommentCount > 0 && (
                <Box display="flex" justifyContent="center" mb={1}>
                  <Button
                    variant="bordered"
                    size="small"
                    onClick={onShowNewComments}
                  >
                    {t('newComments', {
                      defaultValue: '{{count}} new update',
                      count: pendingCommentCount,
                    })}
                  </Button>
                </Box>
              )}
              {sortedComments.map((comment) => (
                <ActivityComment
                  key={comment.id}
                  comment={comment}
                  canDelete={
                    canManage || String(comment.user.id) === String(user.id)
                  }
                  onDelete={() => handleDeleteComment(comment.id)}
                />
              ))}
            </Box>
          )}
        </Box>
      </Card>
    </>
  );
};
